<template>
  <loader-content class="" :ref="(el: any) => tournamentContentLoader = el">
    <div class="tournament-content">
        <div class="btn-join" v-if="props.tournament.status === TournamentStatus.ACTIVE && state.leaderboard && !(state.leaderboard as TournamentLeaderboardDTO|null)?.participant">
          <button @click="join()" class="btn btn-primary w-100"><span class="text-capitalize-first">{{ $t('join') }}</span></button>
        </div>
        <div class="btn-join" v-else-if="props.tournament.status === TournamentStatus.ACTIVE && (store.getters.getUser as UserDTO|null)?.currentTournamentParticipant?.tournamentId !== props.tournament.id">
          <button  @click="join()" class="btn btn-primary w-100 "><span class="text-capitalize-first">{{ $t('play') }}</span></button>
        </div>

      <div class="d-flex flex-column" v-if="props.tournament.type === TournamentType.POKER && props.tournament.status === TournamentStatus.ACTIVE && participant?.pokerCombination ">
        <div class="header ms-0">
          <span class="text-capitalize-first">{{ $t('your_hand') }}</span>
        </div>
        <div class="d-flex flex-column current-combination">
          <span class="combination-name" v-if="participant?.pokerCombination?.combination">{{ $t(`poker_combinations.${participant.pokerCombination.combination}`) }}</span>
          <PokerCombination class="current" :pokerCombination="participant?.pokerCombination"/>
          <span class="bets-to-next-card" v-if="participant?.pokerCombination && participant.pokerCombination.openCards.length < 5">
            {{ $t('bets_to_next_card_prefix') }}
            <img src="/img/coins/gold_coin.svg" alt="coin">
            <span>{{ numberFormatter.formatNumber(participant.pokerCombination.betsToNextCard) }}</span>
            {{ $t('bets_to_next_card_suffix') }}
          </span>

          <div v-if="participant.previousPokerCombination && participant.previousPokerCombination.combination" class="previous-combination">
            <span class="combination-name">{{ $t('previous_hand') }}: {{ $t(`poker_combinations.${participant.previousPokerCombination.combination}`) }}</span>
            <PokerCombination :pokerCombination="participant.previousPokerCombination"/>
          </div>
        </div>

      </div>

      <div class="d-flex flex-column" v-if="participant && !userInTop">
        <div class="header ms-0">
          <span class="text-capitalize-first">{{ $t('your_position') }}</span>
        </div>
        <TournamentLeaderboard :leaderboard="neighbors" :tournament="props.tournament"/>
      </div>
      <div class="d-flex flex-column" v-if="top.length > 0">
        <div class="header ms-0">
          <span class="text-capitalize-first">{{ $t('leaderboard') }}</span>
        </div>
        <TournamentLeaderboard :leaderboard="top" :tournament="props.tournament"/>
      </div>
      <div class="rules">
        <h2>{{$t('rules')}}</h2>
        <div class="info">
          <div class="item" v-if="props.tournament.bankrollRatioType !== 'NONE'">
            <div>{{$t('tournament.description.prize_pool_ratio')}}</div>
            <div>{{$t(`tournament.ratio.${props.tournament.bankrollRatioType}`, {percent: props.tournament.bankrollRatio})}}</div>
          </div>
          <div class="item" v-if="props.tournament.pointsRatioType !== 'NONE'">
            <div>{{$t('tournament.description.score_accrual')}}</div>
            <div>{{$t(`tournament.ratio.${props.tournament.pointsRatioType}`, {percent: props.tournament.pointsRatio})}}</div>
          </div>
          <div class="item">
            <div>{{$t('tournament.description.start_bankroll')}}</div>
            <div>{{numberFormatter.formatNumber(props.tournament.startBankroll)}}</div>
          </div>
          <div class="item">
            <div>{{$t('tournament.description.current_bankroll')}}</div>
            <div>{{numberFormatter.formatNumber(props.tournament.currentBankroll)}}</div>
          </div>
          <div class="item">
            <div>{{$t('tournament.description.winners_count')}}</div>
            <div>{{numberFormatter.formatNumber(props.tournament.winnersCount)}}</div>
          </div>
          <div class="item">
            <div>{{$t('tournament.description.duration')}}</div>
            <div>{{numberFormatter.formatNumber(props.tournament.duration)}} min</div>
          </div>
        </div>
        <div class="description" style="white-space: pre-line">
          {{props.tournament.description}}
        </div>
      </div>
    </div>
  </loader-content>
</template>

<script setup lang="ts">
import {useStore} from "vuex";
import {numberFormatter} from "@/service/NumberFormatter";
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import LoaderContent from "@/components/LoaderContent.vue";
import {api} from "@/service/APIClient";
import TournamentLeaderboardDTO from "@/models/TournamentLeaderboardDTO";
import {reactive} from "@vue/reactivity";
import UserDTO from "@/models/UserDTO";
import TournamentDTO from "@/models/TournamentDTO";
import {TournamentStatus} from "@/models/enum/TournamentStatus";
import TournamentLeaderboard from "@/components/TournamentLeaderboard.vue";
import TournamentParticipantDTO from "@/models/TournamentParticipantDTO";
import {TournamentType} from "@/models/enum/TournamentType";
import PokerCombination from "@/components/PokerCombination.vue";

const props = defineProps<{
  tournament: TournamentDTO
}>()
const store = useStore()
const tournamentContentLoader = ref<InstanceType<typeof LoaderContent> | null>(null)

const top = computed(() => {
  return state.leaderboard?.top ?? []
})

const neighbors = computed(() => {
  return state.leaderboard?.neighbors ?? []
})

const participant = computed((): TournamentParticipantDTO|null => {
  return state.leaderboard?.participant as TournamentParticipantDTO|null
})

const userInTop = computed<boolean>(():boolean => {
  if (!store.getters.isLoggedIn) {
    return true;
  }

  for (const item of top.value) {
    if (item.userId === store.getters.getUser?.id) {
      return true;
    }
  }

  return false;
})

const state = reactive({
  leaderboard: null as TournamentLeaderboardDTO|null,
  leaderboardUpdateInterval: null as number|any,
})

const join = async () => {
  tournamentContentLoader.value?.load(async () => {
    await store.dispatch('joinTournament', props.tournament.id)
  });
}

onUnmounted(() => {
  clearInterval(state.leaderboardUpdateInterval)
})

onMounted(() => {
  tournamentContentLoader.value?.load(async () => {
    state.leaderboard = await api.call.tournaments.leaderboard(props.tournament.id)
  });
  state.leaderboardUpdateInterval = setInterval(async () => {
    state.leaderboard = await api.call.tournaments.leaderboard(props.tournament.id)
  }, 5000)
})

watch(() => props.tournament.id, async (newValue, oldValue) => {
  tournamentContentLoader.value?.load(async () => {
    state.leaderboard = await api.call.tournaments.leaderboard(props.tournament.id)
  });
})


</script>